import { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import type { WorkoutPlanDto } from "../types";
import { deleteAthletePlan, watchAthletePlans } from "../db";
import { PlanEditor } from "./PlanEditor";

/**
 * The athlete's own plans, from the browser.
 *
 * The same plans the phone shows, on the same paths: an edit here appears there, because both are
 * writing the athlete's own subtree, which the rules have always allowed them.
 */
export function AthleteView({ user }: { user: User }) {
  const [plans, setPlans] = useState<WorkoutPlanDto[]>([]);
  const [editing, setEditing] = useState<WorkoutPlanDto | "new" | null>(null);

  useEffect(() => watchAthletePlans(user.uid, setPlans), [user.uid]);

  const name = user.displayName?.trim() || user.email || "your plans";

  if (editing) {
    return (
      <PlanEditor
        athleteUid={user.uid}
        athleteName={name}
        initialPlan={editing === "new" ? undefined : editing}
        onDone={() => setEditing(null)}
      />
    );
  }

  return (
    <section className="card">
      <div className="plan-head">
        <h2>Your plans</h2>
      </div>

      {plans.length === 0 ? (
        <p className="hint">
          No plans yet. Write one here, or generate one in the app and it will show up.
        </p>
      ) : (
        <ul className="athletes">
          {plans.map((plan) => (
            <li key={plan.id}>
              <button className="ghost row" onClick={() => setEditing(plan)}>
                <span>
                  {plan.name || "Untitled"}{" "}
                  <span className="hint">— {plan.days?.length ?? 0} days</span>
                </span>
                <span className="chevron">edit</span>
              </button>
              <button
                className="ghost danger"
                onClick={() => deleteAthletePlan(user.uid, plan.id)}
                title="Delete plan"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <button onClick={() => setEditing("new")}>+ New plan</button>
    </section>
  );
}
